import React, { useRef, useEffect, useState } from "react";
import StatusOption from "./StatusOption";

const QuickStatusModal = ({
  isOpen,
  onClose,
  currentStatus,
  onStatusChange,
  room,
  position = { top: 0, left: 0 },
  availableStatuses: customStatuses,
  modalTitle = "Changer le statut",
}) => {
  const modalRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [isPositioned, setIsPositioned] = useState(false);

  // Liste des statuts disponibles (par défaut pour les modules, peut être surchargée)
  const defaultStatuses = [
    { value: "Good", label: "Good", description: "Module fonctionnel", color: "bg-green-100 text-green-800" },
    { value: "Warning", label: "Warning", description: "Attention requise", color: "bg-yellow-100 text-yellow-800" },
    { value: "À équiper", label: "À équiper", description: "Module à installer", color: "bg-gray-100 text-gray-800" },
    { value: "Unstable", label: "Unstable", description: "Connexion difficile, mais pris en charge", color: "bg-amber-100 text-amber-800" },
    { value: "Autre", label: "Autre", description: "Problème non lié batterie / lost", color: "bg-red-100 text-red-800" },
    // Nouveaux statuts NOK
    { value: "Batterie", label: "Batterie", description: "Problème de batterie", color: "bg-red-100 text-red-700" },
    { value: "N/A", label: "N/A", description: "Statut non applicable", color: "bg-red-100 text-red-500" },
    { value: "Lost", label: "Lost", description: "Module perdu / introuvable", color: "bg-red-100 text-red-900" },
  ];

  const availableStatuses = customStatuses || defaultStatuses;

  // Filtrer les statuts disponibles (exclure le statut actuel)
  const filteredStatuses = availableStatuses.filter((option) => option.value !== currentStatus);

  // Calculer la position optimale de la modal
  useEffect(() => {
    if (isOpen) {
      // Calcul immédiat sans temporisation pour éviter les bugs d'affichage
      if (modalRef.current) {
        const modal = modalRef.current;
        const modalHeight = modal.offsetHeight || 380; // Hauteur estimée si pas encore rendue
        const modalWidth = modal.offsetWidth || 280;
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let newTop = position.top + 8;
        let newLeft = position.left;

        // Vérifier si la modal dépasse en bas
        if (newTop + modalHeight > viewportHeight) {
          newTop = position.top - modalHeight - 8;

          if (newTop < 0) {
            newTop = Math.max(10, (viewportHeight - modalHeight) / 2);
          }
        }

        // Vérifier si la modal dépasse à droite
        if (newLeft + modalWidth > viewportWidth) {
          newLeft = viewportWidth - modalWidth - 10;
        }

        // Vérifier si la modal dépasse à gauche
        if (newLeft < 0) {
          newLeft = 10;
        }

        setAdjustedPosition({ top: newTop, left: newLeft });
        setIsPositioned(true);
      } else {
        // Fallback si le ref n'est pas encore disponible
        setAdjustedPosition(position);
        setIsPositioned(true);
      }
    } else {
      setIsPositioned(false);
    }
  }, [isOpen, position]);

  // Fermer la modal en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleStatusSelect = (newStatus) => {
    onStatusChange(newStatus);
    onClose();
  };

  if (!isOpen) return null;

  return (
    // Popover léger sans overlay plein écran pour éviter les effets de "double hover"
    <div
      ref={modalRef}
      className={`fixed bg-white rounded-lg shadow-2xl border-2 border-gray-300 min-w-[280px] z-[9999] ${isPositioned ? "opacity-100" : "opacity-0"}`}
      style={{
        top: adjustedPosition.top,
        left: adjustedPosition.left,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        transition: "opacity 0.05s ease-out",
      }}
    >
      {/* En-tête */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">{modalTitle}</h3>
            <p className="text-xs text-gray-500">Chambre {room?.room_nb}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Liste des statuts - Tous visibles verticalement */}
      <div className="py-1 bg-white">
        {filteredStatuses.map((status) => (
          <StatusOption key={status.value} status={status} isSelected={currentStatus === status.value} onClick={() => handleStatusSelect(status.value)} />
        ))}
      </div>

      {/* Pied de modal */}
      <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-500">Cliquer sur une option pour l'appliquer</p>
      </div>
    </div>
  );
};

export default QuickStatusModal;
